// Workflow Management Utilities
// Manages step validation and navigation flow

export interface WorkflowStep {
  id: string;
  label: string;
  order: number;
  validate: () => boolean;
  requiredSteps?: string[];
}

export const WORKFLOW_STEPS: Record<string, WorkflowStep> = {
  file_upload: {
    id: 'file_upload',
    label: 'رفع الملف',
    order: 1,
    validate: () => true, // Always accessible
  },
  axis_management: {
    id: 'axis_management',
    label: 'إدارة المحاور',
    order: 2,
    validate: () => {
      // Check if file is uploaded
      const state = localStorage.getItem('survey-storage');
      if (!state) return false;
      const parsed = JSON.parse(state);
      return Boolean(parsed.state?.fileData?.length > 0 && parsed.state?.workshopColumn);
    },
    requiredSteps: ['file_upload']
  },
  scale_config: {
    id: 'scale_config',
    label: 'إعداد المقياس',
    order: 3,
    validate: () => {
      const state = localStorage.getItem('survey-storage');
      if (!state) {
        console.log('❌ scale_config validation: No state found');
        return false;
      }
      const parsed = JSON.parse(state);
      const axes = parsed.state?.axes || {};
      const hasAxes = Object.keys(axes).length > 0;
      const hasQuestions = Object.values(axes).some((questions: unknown) => Array.isArray(questions) && questions.length > 0);
      console.log('🔍 scale_config validation:', { 
        axes, 
        hasAxes, 
        hasQuestions,
        result: hasAxes && hasQuestions 
      });
      return Boolean(hasAxes && hasQuestions);
    },
    requiredSteps: ['file_upload', 'axis_management']
  },
  analysis: {
    id: 'analysis',
    label: 'تحليل البيانات',
    order: 4,
    validate: () => {
      const state = localStorage.getItem('survey-storage');
      if (!state) return false;
      const parsed = JSON.parse(state);
      const scaleMapping = parsed.state?.scaleMapping || {};
      return Boolean(Object.keys(scaleMapping).length > 0);
    },
    requiredSteps: ['file_upload', 'axis_management', 'scale_config']
  },
  dashboard: {
    id: 'dashboard',
    label: 'لوحة المعلومات',
    order: 5,
    validate: () => {
      const state = localStorage.getItem('survey-storage');
      if (!state) return false;
      const parsed = JSON.parse(state);
      return Boolean(parsed.state?.analysisResults);
    },
    requiredSteps: ['file_upload', 'axis_management', 'scale_config', 'analysis']
  },
  export: {
    id: 'export',
    label: 'تصدير النتائج',
    order: 6,
    validate: () => {
      const state = localStorage.getItem('survey-storage');
      if (!state) return false;
      const parsed = JSON.parse(state);
      return Boolean(parsed.state?.analysisResults);
    },
    requiredSteps: ['file_upload', 'axis_management', 'scale_config', 'analysis']
  },
  charts: {
    id: 'charts',
    label: 'الرسوم البيانية',
    order: 6,
    validate: () => {
      const state = localStorage.getItem('survey-storage');
      if (!state) return false;
      const parsed = JSON.parse(state);
      return Boolean(parsed.state?.analysisResults);
    },
    requiredSteps: ['file_upload', 'axis_management', 'scale_config', 'analysis']
  },
  instructions: {
    id: 'instructions',
    label: 'دليل الاستخدام',
    order: 7,
    validate: () => true, // Always accessible
  },
  user_management: {
    id: 'user_management',
    label: 'إدارة المستخدمين',
    order: 8,
    validate: () => true, // Always accessible for admins
  }
};

export function canNavigateToStep(targetStepId: string): { allowed: boolean; reason?: string } {
  const step = WORKFLOW_STEPS[targetStepId];
  
  if (!step) {
    return { allowed: false, reason: 'خطوة غير موجودة' };
  }

  // Check if step validation passes
  if (!step.validate()) {
    return { allowed: false, reason: 'يجب إكمال الخطوات السابقة أولاً' };
  }

  // Check if all required steps are completed
  if (step.requiredSteps) {
    for (const requiredStepId of step.requiredSteps) {
      const requiredStep = WORKFLOW_STEPS[requiredStepId];
      if (requiredStep && !requiredStep.validate()) {
        return { 
          allowed: false, 
          reason: `يجب إكمال: ${requiredStep.label}` 
        };
      }
    }
  }

  return { allowed: true };
}

export function getStepStatus(stepId: string): {
  completed: boolean;
  enabled: boolean;
  current: boolean;
} {
  const step = WORKFLOW_STEPS[stepId];
  if (!step) return { completed: false, enabled: false, current: false };

  const state = localStorage.getItem('survey-storage');
  const currentPage = state ? JSON.parse(state).state?.currentPage : 'file_upload';

  // Check if step is enabled (can be accessed)
  const enabled = step.validate();

  // Check if this is the current step
  const current = currentPage === stepId;

  // Check if step is completed:
  // A step is completed if we are currently on a LATER step and this step is valid
  const currentStepOrder = WORKFLOW_STEPS[currentPage]?.order || 1;
  const completed = step.order < currentStepOrder && enabled;

  return { completed, enabled, current };
}

export function getAllStepsStatus() {
  const state = localStorage.getItem('survey-storage');
  const currentPage = state ? JSON.parse(state).state?.currentPage : 'file_upload';

  return Object.values(WORKFLOW_STEPS)
    .sort((a, b) => a.order - b.order)
    .map(step => ({
      id: step.id,
      label: step.label,
      ...getStepStatus(step.id),
      current: currentPage === step.id
    }));
}
